import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom'
import { get, post } from 'aws-amplify/api'
import { buildPathWithQuery } from '../utils/buildQueryString'
import { buildPatientsApiUrl } from '../utils/patientsApiBaseUrl'
import ScheduleContext from '../context/ScheduleContext'
import getClinicsList from '../utils/getClinicsList'
import {
  isClinicsSearchSuccess,
  mapClinicsSearchResponse
} from '../utils/mapClinicsSearchResponse'
import {
  isScheduleValidationsHardError,
  isScheduleValidationsSuccess,
  mapScheduleValidationsResponse
} from '../utils/mapScheduleValidationsResponse'
import { buildScheduleValidationsQueryParams } from '../utils/buildScheduleValidationsQueryParams'
import errorTrackingService from '../services/errorTrackingService'
import { mswEnabled } from '../mocks/toggles'
import { syncMswForceFlag } from '../mocks/mswForceFlag'
import { fetchVerifyOnboardingStatus } from '../utils/fetchVerifyOnboardingStatus'
import {
  GUEST_LOGIN_HEALTH_TEAM_SKIP_REASONS,
  GUEST_LOGIN_VALIDATION_FAILURE_REASONS,
  logGuestLoginFlow,
  logGuestLoginFlowError
} from '../utils/scheduleGuestLoginDebug'
import {
  clearGuestScheduleSelection,
  formatGuestScheduleDate,
  getGuestScheduleSelection,
  getGuestScheduleSelectionForInit,
  isGuestScheduleFlow,
  isGuestScheduleFlowActive,
  isScheduleFlowExcludedEntry,
  isScheduleGuestPublicPath,
  isGuestFlowServerErrorStatus,
  parseGuestScheduleDate,
  persistGuestScheduleSelection,
  shouldRestoreGuestSelection
} from '../utils/scheduleGuestFlow'
import { keepOnlineBookingPayload } from '../utils/onlinePaymentSchedule'
import { readLabsForService } from '../utils/scheduleAppointmentLabs'
import {
  readReasonsForAppointmentForService,
  readTextReasonForService
} from '../utils/scheduleAppointmentReason'

const queryService = () => {
  const querystring = window.location.search
  const params = new URLSearchParams(querystring)

  return parseInt(params.get('serviceId')) || 1
}

const prevAppointment = () => {
  const querystring = window.location.search
  const params = new URLSearchParams(querystring)
  return params.get('appointmentId')
}

const prevAppointmentStatus = () => {
  const querystring = window.location.search
  const params = new URLSearchParams(querystring)
  return params.get('paid')
}

const queryDoctor = () => {
  const querystring = window.location.search
  const params = new URLSearchParams(querystring)

  return params.get('doctor') || ''
}

const queryDate = () => {
  const querystring = window.location.search
  const params = new URLSearchParams(querystring)

  const date = params.get('date') ? new Date(params.get('date')) : new Date()
  return date
}

const queryAppointmentType = () => {
  const querystring = window.location.search
  const params = new URLSearchParams(querystring)

  const appointmentType = params.get('followUp') ? 'follow-up' : 'new schedule'
  return appointmentType
}

const queryValidTo = () => {
  const querystring = window.location.search
  const params = new URLSearchParams(querystring)
  const validTo = params.get('validTo')

  if (!validTo) return null
  return new Date(`${validTo}T00:00:00`)
}

const ScheduleProvider = ({ children }) => {
  const { getIdTokenClaims, isAuthenticated } = useAuth0()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const [appointmentType] = useState(queryAppointmentType)
  const [followUpValidTo] = useState(queryValidTo)
  const [idService, setIdService] = useState(queryService)
  const prevAppointmentId = prevAppointment()
  const prevAppointmentPaid = prevAppointmentStatus()
  const [appointmentId, setAppointmentId] = useState('')
  const [isPackage, setIsPackage] = useState(false)
  const [medicalRecord, setMedicalRecord] = useState('')
  const [laboratories, setLaboratories] = useState([])
  const [laboratoriesPrice, setLaboratoriesPrice] = useState('')
  const [finalPrice, setFinalPrice] = useState('')
  const [discountLabel, setDiscountLabel] = useState('')
  const [ammountDiscounted, setAmmountDiscounted] = useState(0)
  const [totalPriceToBePaid, setTotalPriceToBePaid] = useState(0)
  const [session, setSession] = useState(0)
  const [numberSessions, setNumberSessions] = useState(1)

  const initialGuestSelection = getGuestScheduleSelectionForInit(queryService())

  const [date, setDate] = useState(
    () => parseGuestScheduleDate(initialGuestSelection?.date) || queryDate()
  )
  const [timeSelect, setTimeSelect] = useState(
    () => initialGuestSelection?.timeSelect || ''
  )
  const [appointment, setAppointment] = useState(null)
  const [hourAndDoctorSelect, setHourAndDoctorSelect] = useState(
    () => {
      const saved = initialGuestSelection?.hourAndDoctorSelect
      if (!saved) return null
      return {
        ...saved,
        date: parseGuestScheduleDate(saved.date) || saved.date
      }
    }
  )
  const [doctor, setDoctor] = useState(() => initialGuestSelection?.doctor)
  const [isEditingSession, setIsEditingSession] = useState(false)
  const [dataPatient, setDataPatient] = useState({})
  const [selectedDatePackage, setSelectedDatePackage] = useState({
    first: 'Pendiente',
    second: 'Pendiente',
    third: 'Pendiente',
    fourth: 'Pendiente',
    fifth: 'Pendiente',
    sixth: 'Pendiente',
    seventh: 'Pendiente'
  })
  const [selectedHourPackage, setSelectedHourPackage] = useState({
    first: '',
    second: '',
    third: '',
    fourth: '',
    fifth: '',
    sixth: '',
    seventh: ''
  })

  const [typeformLink, setTypeformLink] = useState('')
  const [currentView, setCurrentView] = useState('calendar')
  const [paymentMethodSelected, setPaymentMethodSelected] = useState('')
  const [consent, setConsent] = useState('')
  const [terms, setTerms] = useState('')
  const [service, setService] = useState(
    () => initialGuestSelection?.service || {}
  )
  const [reasonsForAppointment, setReasonsForAppointment] = useState()
  const [reason, setReason] = useState('')
  const [coupon, setCoupon] = useState('')
  const [wantToChangeDoctor, setWantToChangeDoctor] = useState(false)
  const [doctorId] = useState(queryDoctor)
  const [refreshData, setRefreshData] = useState(false)
  const [clinicSelect, setClinicSelect] = useState(
    () => initialGuestSelection?.clinicSelect || ''
  )
  const [selectedClinic, setSelectedClinic] = useState(
    () => initialGuestSelection?.selectedClinic || null
  )
  const [membership, setMembership] = useState()
  const [dataAppointmentActive, setDataAppointmentActive] = useState(null)
  const [paymentMethodIdSelected, setPaymentMethodIdSelected] = useState('')
  const [downPaymentAmount, setDownPaymentAmount] = useState(null)
  const [discountRate, setDiscountRate] = useState('')
  const [entrypoint, setEntrypoint] = useState('')
  const [wantsEarlierAppointment, setWantsEarlierAppointment] = useState(false)
  const [geoLocation, setGeoLocation] = useState(null)
  const [clinicsList, setClinicsList] = useState([])
  const [previousClinicSelected, setPreviousClinicSelected] = useState('')
  const [previousClinicNameSelected, setPreviousClinicNameSelected] =
    useState('')
  /** From availability; optional API flag, defaults to true when absent. */
  const [allowChangeDoctor, setAllowChangeDoctor] = useState(true)

  const [tokenAuth, setTokenAuth] = useState('')

  const isLabsService = idService === 10 || idService === 53
  const servicePriceOverride = useMemo(() => {
    if (!isLabsService) return undefined
    return laboratoriesPrice || undefined
  }, [isLabsService, laboratoriesPrice])

  const baseUrl = process.env.REACT_APP_API_BASE_URL

  const getPatientInfo = async (token) => {
    try {
      const resp = post({
        apiName: baseUrl,
        path: '/auth/getPatientInfo',
        options: {
          headers: {
            'content-type': 'application/json',
            Authorization: `Bearer ${token.__raw}`
          },
          body: {}
        }
      })
      const { body } = await resp.response
      const data = await body.json()

      if (data.success === false) {
        const error = new Error(data.message || 'API returned success: false')
        error.isAPIError = true
        error.response = { status: 200, data }
        errorTrackingService.trackAPIError(
          error,
          '/auth/getPatientInfo',
          'POST'
        )
        return undefined
      }

      return data
    } catch (error) {
      errorTrackingService.trackAPIError(error, '/auth/getPatientInfo', 'POST')
      console.log(error)
      return undefined
    }
  }

  const verifyOnboardingStatus = async () => {
    const idToken = await getIdTokenClaims()
    return fetchVerifyOnboardingStatus(idToken)
  }

  const getAppointmentInfo = useCallback(
    async (appointment) => {
      const idToken = await getIdTokenClaims()

      try {
        const resp = get({
          apiName: baseUrl,
          path: `/api/v2/appointments/${appointment}/checkup`,
          options: {
            headers: {
              'content-type': 'application/json',
              Authorization: `Bearer ${idToken.__raw}`
            }
          }
        })
        const { body } = await resp.response
        const data = await body.json()

        if (data.success === false) {
          const error = new Error(
            data.message || 'API returned success: false'
          )
          error.isAPIError = true
          error.response = { status: 200, data }
          errorTrackingService.trackAPIError(
            error,
            `/api/v2/appointments/${appointment}/checkup`,
            'GET'
          )
          return undefined
        }

        return data.data
      } catch (error) {
        errorTrackingService.trackAPIError(
          error,
          `/api/v2/appointments/${appointment}/checkup`,
          'GET'
        )
        console.log(error)
        return undefined
      }
    },
    [getIdTokenClaims, baseUrl]
  )

  const getClinics = useCallback(
    async (lat = null, long = null) => {
      const useSearchEndpoint = isGuestScheduleFlow()

      try {
        let idToken = null
        try {
          idToken = await getIdTokenClaims()
        } catch {
          idToken = null
        }
        const result = await getClinicsList(
          idService,
          lat,
          long,
          idToken,
          useSearchEndpoint ? { variant: 'search' } : undefined
        )

        if (useSearchEndpoint) {
          if (!isClinicsSearchSuccess(result)) return []
          return mapClinicsSearchResponse(result)
        }

        if (result.success === true) return result?.data?.clinics || []
        return []
      } catch (error) {
        if (useSearchEndpoint) {
          throw error
        }
        console.log(error)
        return []
      }
    },
    [getIdTokenClaims, idService]
  )

  const rescheduleAppointment = async (appointmentId, body) => {
    const idToken = await getIdTokenClaims()

    try {
      const resp = post({
        apiName: baseUrl,
        path: `/api/v2/booking/appointments/${appointmentId}/reschedule`,
        options: {
          headers: {
            'content-type': 'application/json',
            Authorization: `Bearer ${idToken.__raw}`,
            'Request-Channel': 'app-web'
          },
          body
        }
      })
      const { body: responseBody } = await resp.response
      const data = await responseBody.json()

      if (data.success === false) {
        const error = new Error(data.message || 'API returned success: false')
        error.isAPIError = true
        error.response = { status: 200, data }
        errorTrackingService.trackAPIError(
          error,
          `/api/v2/booking/appointments/${appointmentId}/reschedule`,
          'POST'
        )
        return undefined
      }

      return data
    } catch (error) {
      errorTrackingService.trackAPIError(
        error,
        `/api/v2/booking/appointments/${appointmentId}/reschedule`,
        'POST'
      )
      console.log(error)
      return undefined
    }
  }

  const getReasonsForAppointment = () => {
    if (idService === 10 || idService === 53) {
      const labs = readLabsForService(idService)
      const payload = labs.length
        ? labs
        : (readReasonsForAppointmentForService(idService) || [])
      setReasonsForAppointment(undefined)
      setLaboratories(payload)
      return
    }

    setLaboratories([])
    setReasonsForAppointment(readReasonsForAppointmentForService(idService))
  }

  const getPriceLaboratories = () => {
    if (!prevAppointmentId && laboratories) {
      const prices = laboratories.map((laboratory) => {
        return laboratory.price
      })

      const totalPrice = prices.reduce(
        (accumulator, currentValue) => accumulator + currentValue,
        0
      )

      setLaboratoriesPrice(totalPrice)
    }
  }

  const buildScheduleValidationsParams = (parameters = {}) =>
    buildScheduleValidationsQueryParams(parameters, {
      isGuestFlowActive: isGuestScheduleFlowActive()
    })

  const logScheduleValidationsFailure = (failureReason, details = {}) => {
    logGuestLoginFlowError('Schedule Validations Failed', {
      failure_reason: failureReason,
      endpoint: '/v1/schedules/validations',
      ...details
    })
  }

  const getValidationAppointmentActive = async (parameters) => {
    if (mswEnabled()) {
      syncMswForceFlag()
    }

    const endpoint = buildPatientsApiUrl('/v1/schedules/validations')
    if (!endpoint) {
      logScheduleValidationsFailure(
        GUEST_LOGIN_VALIDATION_FAILURE_REASONS.MISSING_API_BASE_URL
      )
      console.error(
        'Missing REACT_APP_PATIENTS_API_BASE_URL for schedule validations'
      )
      return { success: false, failureReason: 'missing_api_base_url' }
    }

    const idToken = await getIdTokenClaims()
    if (!idToken?.__raw) {
      logScheduleValidationsFailure(
        GUEST_LOGIN_VALIDATION_FAILURE_REASONS.MISSING_AUTH_TOKEN,
        { query_params: buildScheduleValidationsParams(parameters) }
      )
      return { success: false, failureReason: 'missing_auth_token' }
    }

    const queryParams = buildScheduleValidationsParams(parameters)
    const url = buildPathWithQuery(endpoint, queryParams)

    try {
      const response = await fetch(url, {
        headers: {
          'content-type': 'application/json',
          Authorization: `Bearer ${idToken.__raw}`
        }
      })
      const raw = await response.json()
      const data = mapScheduleValidationsResponse(raw)

      if (!response.ok) {
        const error = new Error(data?.message || 'Error al validar cita activa')
        error.isAPIError = true
        error.response = { status: response.status, data }
        errorTrackingService.trackAPIError(error, endpoint, 'GET')
        setDataAppointmentActive(data?.data || null)

        logScheduleValidationsFailure(
          GUEST_LOGIN_VALIDATION_FAILURE_REASONS.HTTP_ERROR,
          {
            status: response.status,
            code: data?.code ?? null,
            message: data?.message || null,
            response_data: data?.data ?? data,
            query_params: queryParams,
            url
          }
        )

        if (isGuestFlowServerErrorStatus(response.status)) {
          return {
            success: false,
            isServerError: true,
            status: response.status,
            code: data?.code,
            message: data?.message,
            data: data?.data ?? null,
            failureReason: 'http_error'
          }
        }

        return {
          success: false,
          status: response.status,
          code: data?.code,
          message: data?.message,
          data: data?.data ?? null,
          failureReason: 'http_error'
        }
      }

      if (isScheduleValidationsHardError(raw)) {
        const error = new Error(raw?.message || 'Validación sin datos')
        error.isAPIError = true
        error.response = { status: response.status, data: raw }
        errorTrackingService.trackAPIError(error, endpoint, 'GET')

        logScheduleValidationsFailure(
          GUEST_LOGIN_VALIDATION_FAILURE_REASONS.VALIDATION_DATA_NULL,
          {
            status: response.status,
            code: raw?.code ?? null,
            message: raw?.message || null,
            query_params: queryParams,
            url
          }
        )

        setDataAppointmentActive(null)

        return {
          success: false,
          code: raw?.code,
          message: raw?.message,
          data: null,
          failureReason: 'validation_data_null'
        }
      }

      if (!isScheduleValidationsSuccess(raw)) {
        setDataAppointmentActive(data?.data || null)

        return data
      }

      const paymentMethods = data?.data?.service?.paymentMethods || []

      // Empty paymentMethods means the appointment can be booked directly
      // via /api/v3/booking/schedule-appointments (no payment step).
      const preselectedMethod =
        Array.isArray(paymentMethods) && paymentMethods.length > 1
          ? paymentMethods.find((method) => method?.type === 'Online')
          : Array.isArray(paymentMethods)
            ? paymentMethods[0] || null
            : null

      if (preselectedMethod?.downPaymentDetail) {
        setDownPaymentAmount(preselectedMethod.downPaymentDetail?.downPayment)
      }

      setPaymentMethodIdSelected(preselectedMethod?._id ?? null)

      return data
    } catch (error) {
      const statusCode = Number(
        error?.response?.status ?? error?.$metadata?.httpStatusCode
      )
      errorTrackingService.trackAPIError(error, endpoint, 'GET')

      logScheduleValidationsFailure(
        GUEST_LOGIN_VALIDATION_FAILURE_REASONS.NETWORK_ERROR,
        {
          status: statusCode || null,
          message: error?.message || null,
          query_params: queryParams,
          url
        }
      )

      console.log('Error in getValidationAppointmentActive:', error)
      setDataAppointmentActive(null)

      if (isGuestFlowServerErrorStatus(statusCode)) {
        return {
          success: false,
          isServerError: true,
          status: statusCode,
          message: error?.message,
          failureReason: 'network_error'
        }
      }

      return {
        success: false,
        message: error?.message,
        failureReason: 'network_error'
      }
    }
  }

  const checkDoctorInHealthTeam = useCallback(
    async (doctorId, serviceId) => {
      if (!doctorId || !serviceId) {
        logGuestLoginFlow('Schedule Guest Login Health Team Skipped', {
          skip_reason:
            GUEST_LOGIN_HEALTH_TEAM_SKIP_REASONS.MISSING_DOCTOR_OR_SERVICE,
          doctor_id: doctorId || null,
          id_service: serviceId || null
        })
        return { success: false }
      }

      const idToken = await getIdTokenClaims()
      const endpoint = buildPatientsApiUrl(
        `/v1/patients/health_team/doctors/${encodeURIComponent(doctorId)}/exists`
      )

      if (!endpoint) {
        logGuestLoginFlow('Schedule Guest Login Health Team Skipped', {
          skip_reason:
            GUEST_LOGIN_HEALTH_TEAM_SKIP_REASONS.MISSING_PATIENTS_API_BASE_URL,
          doctor_id: doctorId,
          id_service: serviceId
        })
        console.error(
          'Missing REACT_APP_PATIENTS_API_BASE_URL for health team doctor check'
        )
        return { success: false }
      }

      const url = buildPathWithQuery(endpoint, {
        plenna_service_id: serviceId
      })

      logGuestLoginFlow('Schedule Guest Login Health Team API Request', {
        doctor_id: doctorId,
        id_service: serviceId,
        endpoint: url
      })

      try {
        const response = await fetch(url, {
          headers: {
            'content-type': 'application/json',
            Authorization: `Bearer ${idToken.__raw}`
          }
        })

        logGuestLoginFlow('Schedule Guest Login Health Team API Response', {
          doctor_id: doctorId,
          id_service: serviceId,
          status: response.status,
          endpoint: url
        })

        if (response.status === 423) {
          return { success: false, isHealthTeamMismatch: true }
        }

        if (isGuestFlowServerErrorStatus(response.status)) {
          const error = new Error(
            'Error del servidor al verificar equipo de salud'
          )
          error.isAPIError = true
          error.response = { status: response.status }
          errorTrackingService.trackAPIError(error, url, 'GET')
          return { success: false, isServerError: true, status: response.status }
        }

        if (!response.ok) {
          const data = await response.json().catch(() => ({}))
          const error = new Error(
            data?.message || 'Error al verificar equipo de salud'
          )
          error.isAPIError = true
          error.response = { status: response.status, data }
          errorTrackingService.trackAPIError(error, url, 'GET')
          return { success: false }
        }

        return { success: true }
      } catch (error) {
        errorTrackingService.trackAPIError(error, url, 'GET')
        console.log('Error in checkDoctorInHealthTeam:', error)
        const status = Number(
          error?.response?.status ?? error?.$metadata?.httpStatusCode
        )
        if (isGuestFlowServerErrorStatus(status)) {
          return { success: false, isServerError: true, status }
        }
        return { success: false, isServerError: true }
      }
    },
    [getIdTokenClaims]
  )

  const getScheduleValidations = useCallback(
    async (parameters, tokenOverride = null) => {
      if (mswEnabled()) {
        syncMswForceFlag()
      }

      const idToken = await getIdTokenClaims()

      const endpoint = buildPatientsApiUrl('/v1/schedules/validations')
      if (!endpoint) {
        console.error(
          'Missing REACT_APP_PATIENTS_API_BASE_URL for schedule validations'
        )
        return undefined
      }
      const url = buildPathWithQuery(
        endpoint,
        buildScheduleValidationsParams(parameters)
      )

      try {
        const response = await fetch(url, {
          headers: {
            'content-type': 'application/json',
            Authorization: `Bearer ${idToken.__raw}`
          }
        })
        const raw = await response.json()
        const data = mapScheduleValidationsResponse(raw)

        if (!response.ok) {
          const error = new Error(
            data?.message || 'Error al validar disponibilidad'
          )
          error.isAPIError = true
          error.response = { status: response.status, data }
          errorTrackingService.trackAPIError(error, endpoint, 'GET')
          return undefined
        }

        if (isScheduleValidationsHardError(raw)) {
          return undefined
        }

        return data
      } catch (error) {
        errorTrackingService.trackAPIError(error, endpoint, 'GET')
        console.log(error)
        return undefined
      }
    },
    [getIdTokenClaims]
  )

  const getAvailability = useCallback(
    async (parameters, tokenOverride = null, options = {}) => {
      let authToken = tokenOverride?.__raw ? tokenOverride : null
      if (!authToken?.__raw) {
        try {
          authToken = await getIdTokenClaims()
        } catch {
          authToken = null
        }
      }

      // Light is guest-only — never use it when an Auth0 token is available
      // (e.g. guest UI still active during login-checkout continuation).
      const useLightEndpoint = options.variant === 'light' && !authToken?.__raw
      const availabilityPath = useLightEndpoint
        ? '/v1/scheduling/availability/light'
        : '/v1/scheduling/availability'

      const endpoint = buildPatientsApiUrl(availabilityPath)
      if (!endpoint) {
        console.error(
          'Missing REACT_APP_PATIENTS_API_BASE_URL for scheduling availability'
        )
        return undefined
      }
      const url = buildPathWithQuery(endpoint, parameters)
      const headers = { 'content-type': 'application/json' }
      if (authToken?.__raw) {
        headers.Authorization = `Bearer ${authToken.__raw}`
      }

      try {
        const response = await fetch(url, { headers })
        const data = await response.json()

        if (!response.ok) {
          const error = new Error(
            data?.message || 'Error al obtener disponibilidad'
          )
          error.isAPIError = true
          error.response = { status: response.status, data }
          errorTrackingService.trackAPIError(error, endpoint, 'GET')
          return undefined
        }

        return data
      } catch (error) {
        errorTrackingService.trackAPIError(error, endpoint, 'GET')
        console.log(error)
        return undefined
      }
    },
    [getIdTokenClaims]
  )

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const queryKeys = [
      'brand',
      'business_type',
      'unique_code',
      'uniqueCode',
      'isAmazon'
    ]
    const hasQueryParams = queryKeys.some((key) => params.has(key))

    const isGuestCalendarioPath =
      isScheduleGuestPublicPath(location.pathname) && !isAuthenticated

    if (hasQueryParams && !isGuestCalendarioPath) {
      const newQueryString = params.toString()
      navigate(`/home?${newQueryString}`)
    }
  }, [navigate, location.pathname, isAuthenticated])

  useEffect(() => {
    setEntrypoint(searchParams.get('medium'))
  }, [searchParams])

  useEffect(() => {
    const wantToChangeDoctorParam = searchParams.get('wantToChangeDoctor')
    if (wantToChangeDoctorParam === 'true') {
      setWantToChangeDoctor(true)
    }
  }, [searchParams])

  useEffect(() => {
    setReason(readTextReasonForService(idService) || '')
    getReasonsForAppointment()
    // eslint-disable-next-line
  }, [idService]);

  useEffect(() => {
    const sessionsCount = service?.numberSessions
    const isServicePackage = Boolean(sessionsCount && sessionsCount > 1)
    const hasLoadedService = Boolean(service?.title || service?._id)

    setIsPackage(isServicePackage)
    setNumberSessions(sessionsCount || 1)

    const isSessionsPath = location.pathname === '/calendario/sesiones'
    const isCalendarPath =
      location.pathname === '/calendario' ||
      location.pathname === '/calendario/'
    const fromSessions = location.state?.fromSessions
    if (
      isServicePackage &&
      hasLoadedService &&
      (isAuthenticated || isGuestScheduleFlow()) &&
      !prevAppointmentId &&
      !fromSessions &&
      !isSessionsPath &&
      isCalendarPath
    ) {
      const search = window.location.search || ''
      navigate(`/calendario/sesiones${search}`)
    }
  }, [
    navigate,
    isAuthenticated,
    prevAppointmentId,
    service?.numberSessions,
    location.pathname,
    location.state?.fromSessions
  ])

  useEffect(() => {
    if (!prevAppointmentId) {
      if (idService === 10 || idService === 53) {
        getPriceLaboratories()
      }
    }
    // eslint-disable-next-line
  }, [laboratories, prevAppointmentId]);

  useEffect(() => {
    getIdTokenClaims()
      .then((resp) => {
        if (resp !== undefined) {
          setTokenAuth(resp)
        }
      })
      .catch((error) => console.log(error))
  }, [getIdTokenClaims])

  useEffect(() => {
    if (isScheduleFlowExcludedEntry(searchParams)) return
    // Restore after guest login even when the guest-flow boolean was cleared
    // (login continuation keeps auth mode + pending → isGuestScheduleFlowActive).
    if (!isGuestScheduleFlowActive()) return

    const saved = getGuestScheduleSelection()
    if (saved?.idService && Number(saved.idService) !== Number(idService)) {
      clearGuestScheduleSelection()
      return
    }

    if (!shouldRestoreGuestSelection(idService)) return

    const restoredDate = parseGuestScheduleDate(saved.date)
    if (restoredDate) setDate(restoredDate)
    if (saved.timeSelect) setTimeSelect(saved.timeSelect)
    if (saved.hourAndDoctorSelect) {
      setHourAndDoctorSelect({
        ...saved.hourAndDoctorSelect,
        date:
          parseGuestScheduleDate(saved.hourAndDoctorSelect.date) ||
          saved.hourAndDoctorSelect.date
      })
    }
    if (saved.clinicSelect) setClinicSelect(saved.clinicSelect)
    if (saved.doctor) setDoctor(saved.doctor)
    if (saved.service) setService(saved.service)
    if (saved.selectedClinic) setSelectedClinic(saved.selectedClinic)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idService])

  useEffect(() => {
    if (!isGuestScheduleFlowActive() || !idService) return

    // Never persist a partial state that would wipe a saved slot/date after Auth0.
    const hasCheckoutSelection = Boolean(
      date && hourAndDoctorSelect?.doctorId
    )
    const hasClinicOnly = Boolean(clinicSelect) && !hourAndDoctorSelect
    if (!hasCheckoutSelection && !hasClinicOnly) return

    const saved = getGuestScheduleSelection()
    if (
      saved?.hourAndDoctorSelect?.doctorId &&
      !hourAndDoctorSelect?.doctorId
    ) {
      return
    }

    persistGuestScheduleSelection({
      idService,
      clinicSelect,
      date: formatGuestScheduleDate(date),
      timeSelect,
      hourAndDoctorSelect,
      doctor,
      service,
      selectedClinic
    })
  }, [
    idService,
    clinicSelect,
    date,
    timeSelect,
    hourAndDoctorSelect,
    doctor,
    service,
    selectedClinic
  ])

  useEffect(() => {
    if (tokenAuth !== '' || refreshData) {
      getPatientInfo(tokenAuth)
        .then((resp) => {
          if (!resp?.data) return

          setDataPatient((prev) => keepOnlineBookingPayload(prev, resp.data))

          window.dataLayer = window.dataLayer || []
          window.dataLayer.push({
            userId: resp.data._id || ''
          })
        })
        .catch((error) => {
          console.log(error)
        })
    }
    // eslint-disable-next-line
  }, [tokenAuth, refreshData]);

  return (
    <ScheduleContext.Provider
      value={{
        appointmentType,
        followUpValidTo,
        reason,
        setReason,
        appointmentId,
        setAppointmentId,
        setMedicalRecord,
        medicalRecord,
        laboratories,
        laboratoriesPrice,
        dataPatient,
        setDataPatient,
        doctorId,
        doctor,
        setDoctor,
        tokenAuth,
        service,
        setService,
        isPackage,
        setIsPackage,
        idService,
        setIdService,
        date,
        setDate,
        timeSelect,
        setTimeSelect,
        appointment,
        setAppointment,
        hourAndDoctorSelect,
        setHourAndDoctorSelect,
        isEditingSession,
        setIsEditingSession,
        finalPrice,
        setFinalPrice,
        discountLabel,
        setDiscountLabel,
        ammountDiscounted,
        setAmmountDiscounted,
        totalPriceToBePaid,
        setTotalPriceToBePaid,
        typeformLink,
        setTypeformLink,
        currentView,
        setCurrentView,
        paymentMethodSelected,
        setPaymentMethodSelected,
        consent,
        setConsent,
        terms,
        setTerms,
        session,
        setSession,
        selectedDatePackage,
        setSelectedDatePackage,
        selectedHourPackage,
        setSelectedHourPackage,
        reasonsForAppointment,
        setReasonsForAppointment,
        verifyOnboardingStatus,
        setRefreshData,
        prevAppointmentId,
        getAppointmentInfo,
        rescheduleAppointment,
        coupon,
        setCoupon,
        wantToChangeDoctor,
        setWantToChangeDoctor,
        servicePriceOverride,
        clinicSelect,
        setClinicSelect,
        numberSessions,
        setNumberSessions,
        prevAppointmentPaid,
        membership,
        setMembership,
        getValidationAppointmentActive,
        checkDoctorInHealthTeam,
        getScheduleValidations,
        getAvailability,
        dataAppointmentActive,
        paymentMethodIdSelected,
        setPaymentMethodIdSelected,
        downPaymentAmount,
        discountRate,
        setDiscountRate,
        entrypoint,
        wantsEarlierAppointment,
        setWantsEarlierAppointment,
        geoLocation,
        setGeoLocation,
        getClinics,
        clinicsList,
        setClinicsList,
        previousClinicSelected,
        setPreviousClinicSelected,
        previousClinicNameSelected,
        setPreviousClinicNameSelected,
        selectedClinic,
        setSelectedClinic,
        allowChangeDoctor,
        setAllowChangeDoctor
      }}
    >
      {children}
    </ScheduleContext.Provider>
  )
}

export default ScheduleProvider
